import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, Clock, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

const MaintenancePage = ({ message: propMessage, etaMessage: propEta, onRetry }) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState(propMessage || '');
  const [etaMessage, setEtaMessage] = useState(propEta || '');
  const [checking, setChecking] = useState(false);
  
  useEffect(() => {
    // Read from localStorage if not passed as props
    if (!propMessage) {
      const storedMessage = localStorage.getItem('maintenance_message');
      const storedEta = localStorage.getItem('maintenance_eta');
      if (storedMessage) setMessage(storedMessage);
      if (storedEta) setEtaMessage(storedEta);
    }
    
    // Check maintenance status periodically
    const checkStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/maintenance/status`);
        const data = await response.json();
        
        if (!data.is_active) {
          // Maintenance is over, clear storage and redirect
          localStorage.removeItem('maintenance_message');
          localStorage.removeItem('maintenance_eta');
          window.location.href = '/';
        }
      } catch (error) {
        console.error('Failed to check maintenance status');
      }
    };
    
    const interval = setInterval(checkStatus, 30000);
    return () => clearInterval(interval);
  }, [propMessage]);

  const handleRetry = async () => {
    setChecking(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/maintenance/status`);
      const data = await response.json();
      
      if (!data.is_active) {
        localStorage.removeItem('maintenance_message');
        localStorage.removeItem('maintenance_eta');
        window.location.href = '/';
      } else {
        setMessage(data.message || message);
        setEtaMessage(data.eta_message || etaMessage);
      }
    } catch (error) {
      console.error('Failed to check status');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center p-4">
      <div className="text-center max-w-lg">
        {/* Animated Icon */}
        <div className="relative mb-8">
          <div className="w-32 h-32 mx-auto bg-blue-500/20 rounded-full flex items-center justify-center">
            <Settings className="w-16 h-16 text-blue-400 animate-spin" style={{ animationDuration: '3s' }} />
          </div>
          <div className="absolute inset-0 w-32 h-32 mx-auto border-4 border-blue-400/30 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
        </div>
        
        {/* Title */}
        <h1 className="text-4xl font-bold text-white mb-4">
          {t('maintenance.title')}
        </h1>
        
        {/* Message */}
        <p className="text-xl text-gray-300 mb-6">
          {message || t('maintenance.message_default')}
        </p>
        
        {/* ETA */}
        {etaMessage && (
          <div className="flex items-center justify-center gap-2 text-blue-300 mb-8">
            <Clock className="w-5 h-5" />
            <span>{etaMessage}</span>
          </div>
        )}
        
        {/* Info Box */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-white mb-2">{t('maintenance.whats_happening')}</h3>
          <ul className="text-gray-300 text-left space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-blue-400">•</span>
              <span>{t('maintenance.team_working')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400">•</span>
              <span>{t('maintenance.data_safe')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400">•</span>
              <span>{t('maintenance.service_restored')}</span>
            </li>
          </ul>
        </div>
        
        {/* Retry Button */}
        <Button
          onClick={handleRetry}
          disabled={checking}
          className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
          {checking ? t('maintenance.checking') : t('maintenance.check_again')}
        </Button>
        
        {/* Contact Info */}
        <p className="text-gray-400 text-sm mt-8">
          {t('maintenance.need_help')}{' '}
          <a href="mailto:support@movematebend.co.uk" className="text-blue-400 hover:underline">
            support@movematebend.co.uk
          </a>
        </p>
      </div>
    </div>
  );
};

export default MaintenancePage;
